import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateRulesOptions,
  BulkCreateRulesResponse,
  BulkDeleteRulesResponse,
  BulkUpdateRuleTagsApplicationErrors,
  BulkUpdateRuleTagsByFilterApplicationErrors,
  BulkUpdateRuleTagsByFilterOptions,
  BulkUpdateRuleTagsByFilterResponse,
  BulkUpdateRuleTagsOptions,
  BulkUpdateRuleTagsResponse,
  BulkUpdateRulesOptions,
  BulkUpdateRulesResponse,
  CalculateServiceFeesOptions,
  CalculateServiceFeesResponse,
  ListRulesOptions,
  ListRulesResponse,
  MaskedRule,
  Order,
  QueryRulesResponse,
  Rule,
  RuleCreatedEnvelope,
  RuleDeletedEnvelope,
  RuleQuery,
  RuleUpdatedEnvelope,
  RulesQueryBuilder,
  UpdateRule,
  bulkCreateRules as universalBulkCreateRules,
  bulkDeleteRules as universalBulkDeleteRules,
  bulkUpdateRuleTags as universalBulkUpdateRuleTags,
  bulkUpdateRuleTagsByFilter as universalBulkUpdateRuleTagsByFilter,
  bulkUpdateRules as universalBulkUpdateRules,
  calculateServiceFees as universalCalculateServiceFees,
  createRule as universalCreateRule,
  deleteRule as universalDeleteRule,
  getRule as universalGetRule,
  listRules as universalListRules,
  queryRules as universalQueryRules,
  typedQueryRules as universalTypedQueryRules,
  updateRule as universalUpdateRule,
} from './service-fees-v1-rule-service-fees.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function calculateServiceFees(
  httpClient: HttpClient
): CalculateServiceFeesSignature {
  return (
    order: NonNullablePaths<Order, `currency` | `priceSummary`, 2>,
    options?: CalculateServiceFeesOptions
  ) =>
    universalCalculateServiceFees(
      order,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateServiceFeesSignature {
  /**
   * The `calculateServiceFees()` function returns a Promise that resolves to an array containing the calculated fees.
   *
   *
   * The specified order information is evaluated against all rules created for the site. If the rule conditions are met, the service fee set in the rule is applied. Otherwise, no service fee is added.
   * @param - Order information needed to evaluate the rules and calculate the relevant fees.
   */
  (
    order: NonNullablePaths<Order, `currency` | `priceSummary`, 2>,
    options?: CalculateServiceFeesOptions
  ): Promise<
    NonNullablePaths<
      CalculateServiceFeesResponse,
      | `calculatedFees`
      | `calculatedFees.${number}.ruleId`
      | `calculatedFees.${number}.name`
      | `calculatedFees.${number}.fee.value`
      | `calculatedFees.${number}.fee.currency`,
      5
    >
  >;
}

export function createRule(httpClient: HttpClient): CreateRuleSignature {
  return (rule: NonNullablePaths<Rule, `enabled` | `name`, 2>) =>
    universalCreateRule(
      rule,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateRuleSignature {
  /**
   * The `createRule()` function returns a Promise that resolves to the created rule.
   *
   *
   * To create multiple rules at once, use the `bulkCreateRules()` function.
   * @param - Rule to create.
   * @returns Created rule
   */
  (rule: NonNullablePaths<Rule, `enabled` | `name`, 2>): Promise<
    NonNullablePaths<
      Rule,
      | `amount.value`
      | `amount.currency`
      | `fixedFee.value`
      | `fixedFee.currency`
      | `condition.number.value`
      | `condition.number.operation`
      | `condition.list.values`
      | `condition.path`
      | `condition.orderFieldPath`
      | `condition.expectedType.value`
      | `condition.expectedFieldType`
      | `conditionTree.operator`
      | `conditionsType`
      | `conditionType`
      | `roundingStrategy`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function getRule(httpClient: HttpClient): GetRuleSignature {
  return (ruleId: string) =>
    universalGetRule(
      ruleId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRuleSignature {
  /**
   * The `createRule()` function returns a Promise that resolves to the retrieved rule.
   * @param - ID of the rule to retrieve.
   * @returns The retrieved rule.
   */
  (ruleId: string): Promise<
    NonNullablePaths<
      Rule,
      | `amount.value`
      | `amount.currency`
      | `fixedFee.value`
      | `fixedFee.currency`
      | `condition.number.value`
      | `condition.number.operation`
      | `condition.list.values`
      | `condition.path`
      | `condition.orderFieldPath`
      | `condition.expectedType.value`
      | `condition.expectedFieldType`
      | `conditionTree.operator`
      | `conditionsType`
      | `conditionType`
      | `roundingStrategy`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function updateRule(httpClient: HttpClient): UpdateRuleSignature {
  return (_id: string, rule: NonNullablePaths<UpdateRule, `revision`, 2>) =>
    universalUpdateRule(
      _id,
      rule,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateRuleSignature {
  /**
   * The `updateRule()` function returns a Promise that resolves to the updated rule.
   *
   *
   * Each time the task is updated, `revision` increments by 1. The existing `revision` must be included when updating the task. This ensures you're working with the latest task and prevents unintended overwrites.
   *
   * To update multiple rules at once, use the `bulkUpdateRules()` function.
   * @param - Rule ID.
   * @returns Updated rule.
   */
  (_id: string, rule: NonNullablePaths<UpdateRule, `revision`, 2>): Promise<
    NonNullablePaths<
      Rule,
      | `amount.value`
      | `amount.currency`
      | `fixedFee.value`
      | `fixedFee.currency`
      | `condition.number.value`
      | `condition.number.operation`
      | `condition.list.values`
      | `condition.path`
      | `condition.orderFieldPath`
      | `condition.expectedType.value`
      | `condition.expectedFieldType`
      | `conditionTree.operator`
      | `conditionsType`
      | `conditionType`
      | `roundingStrategy`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteRule(httpClient: HttpClient): DeleteRuleSignature {
  return (ruleId: string) =>
    universalDeleteRule(
      ruleId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteRuleSignature {
  /**
   * The `deleteRule()` function returns a Promise that resolves to void.
   *
   *
   * To delete multiple rules at once, use the `bulkDeleteRules()` function.
   * @param - ID of the rule to delete.
   */
  (ruleId: string): Promise<void>;
}

export function listRules(httpClient: HttpClient): ListRulesSignature {
  return (options?: ListRulesOptions) =>
    universalListRules(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListRulesSignature {
  /**
   * The `listRules()` function returns a Promise that resolves to an array of the retrieved rules.
   *
   *
   * You can filter by location or app that the rules are associated with.
   */
  (options?: ListRulesOptions): Promise<
    NonNullablePaths<
      ListRulesResponse,
      | `rules`
      | `rules.${number}.amount.value`
      | `rules.${number}.amount.currency`
      | `rules.${number}.fixedFee.value`
      | `rules.${number}.fixedFee.currency`
      | `rules.${number}.condition.number.value`
      | `rules.${number}.condition.number.operation`
      | `rules.${number}.condition.path`
      | `rules.${number}.condition.orderFieldPath`
      | `rules.${number}.condition.expectedType.value`
      | `rules.${number}.condition.expectedFieldType`
      | `rules.${number}.conditionTree.operator`
      | `rules.${number}.conditionsType`
      | `rules.${number}.conditionType`
      | `rules.${number}.roundingStrategy`,
      6
    >
  >;
}

export function queryRules(httpClient: HttpClient): QueryRulesSignature {
  return () =>
    universalQueryRules(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryRulesSignature {
  /**
   * Creates a query to retrieve a list of rules.
   *
   *
   * The `queryRules()` function builds a query to retrieve a list of up to 1,000 rules and returns a `RulesQueryBuilder` object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](/service-fees/rules-query-builder/find) function.
   *
   * You can refine the query by chaining `RulesQueryBuilder` functions onto the query. `RulesQueryBuilder` functions enable you to sort, filter, and control the results that `queryRules()` returns. The functions that are chained to `queryRules()` are applied in the order they are called.
   *
   * `queryRules()` runs with the following `RulesQueryBuilder` defaults that you can override:
   *
   * * [`limit(50)`](/service-fees/rules-query-builder/limit)
   * * [`ascending('entityId')`](/service-fees/rules-query-builder/ascending)
   *
   * The following `QueryRulesBuilder` functions are supported for the `queryRules()` function. For a full description of the Rules object, see the object returned for the [`items`](/service-fees/rules-query-result/items) property in `RulesQueryResult`.
   */
  (): RulesQueryBuilder;
}

export function typedQueryRules(
  httpClient: HttpClient
): TypedQueryRulesSignature {
  return (query: RuleQuery) =>
    universalTypedQueryRules(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryRulesSignature {
  /** */
  (query: RuleQuery): Promise<
    NonNullablePaths<
      QueryRulesResponse,
      | `rules`
      | `rules.${number}.amount.value`
      | `rules.${number}.amount.currency`
      | `rules.${number}.fixedFee.value`
      | `rules.${number}.fixedFee.currency`
      | `rules.${number}.condition.number.value`
      | `rules.${number}.condition.number.operation`
      | `rules.${number}.condition.path`
      | `rules.${number}.condition.orderFieldPath`
      | `rules.${number}.condition.expectedType.value`
      | `rules.${number}.condition.expectedFieldType`
      | `rules.${number}.conditionTree.operator`
      | `rules.${number}.conditionsType`
      | `rules.${number}.conditionType`
      | `rules.${number}.roundingStrategy`,
      6
    >
  >;
}

export function bulkCreateRules(
  httpClient: HttpClient
): BulkCreateRulesSignature {
  return (rules: Rule[], options?: BulkCreateRulesOptions) =>
    universalBulkCreateRules(
      rules,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateRulesSignature {
  /**
   * The `bulkCreateRules()` function returns a Promise that resolves to the created rules.
   *
   *
   * To create only one rule, use the `createRule()` function.
   * @param - Rules to create.
   */
  (rules: Rule[], options?: BulkCreateRulesOptions): Promise<
    NonNullablePaths<
      BulkCreateRulesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.rule.amount.value`
      | `results.${number}.rule.amount.currency`
      | `results.${number}.rule.fixedFee.value`
      | `results.${number}.rule.fixedFee.currency`
      | `results.${number}.rule.condition.path`
      | `results.${number}.rule.condition.orderFieldPath`
      | `results.${number}.rule.condition.expectedFieldType`
      | `results.${number}.rule.conditionTree.operator`
      | `results.${number}.rule.conditionsType`
      | `results.${number}.rule.conditionType`
      | `results.${number}.rule.roundingStrategy`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateRules(
  httpClient: HttpClient
): BulkUpdateRulesSignature {
  return (rules: MaskedRule[], options?: BulkUpdateRulesOptions) =>
    universalBulkUpdateRules(
      rules,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRulesSignature {
  /**
   * The `bulkUpdateRules()` function returns a Promise that resolves to the updated rules.
   *
   *
   * Each time the task is updated, `revision` increments by 1. The existing `revision` must be included when updating the task. This ensures you're working with the latest task and prevents unintended overwrites.
   *
   * To update only one rule, use the `updateRule()` function.
   * @param - Masked rules to update.
   */
  (rules: MaskedRule[], options?: BulkUpdateRulesOptions): Promise<
    NonNullablePaths<
      BulkUpdateRulesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.rule.amount.value`
      | `results.${number}.rule.amount.currency`
      | `results.${number}.rule.fixedFee.value`
      | `results.${number}.rule.fixedFee.currency`
      | `results.${number}.rule.condition.path`
      | `results.${number}.rule.condition.orderFieldPath`
      | `results.${number}.rule.condition.expectedFieldType`
      | `results.${number}.rule.conditionTree.operator`
      | `results.${number}.rule.conditionsType`
      | `results.${number}.rule.conditionType`
      | `results.${number}.rule.roundingStrategy`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteRules(
  httpClient: HttpClient
): BulkDeleteRulesSignature {
  return (ruleIds: string[]) =>
    universalBulkDeleteRules(
      ruleIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteRulesSignature {
  /**
   * The `bulkDeleteRules()` function returns a Promise that resolves to the deleted rules.
   *
   *
   * To delete only one rule, use the `deleteRule()` function.
   * @param - IDs of the rules to delete.
   */
  (ruleIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteRulesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.rule.amount.value`
      | `results.${number}.rule.amount.currency`
      | `results.${number}.rule.fixedFee.value`
      | `results.${number}.rule.fixedFee.currency`
      | `results.${number}.rule.condition.path`
      | `results.${number}.rule.condition.orderFieldPath`
      | `results.${number}.rule.condition.expectedFieldType`
      | `results.${number}.rule.conditionTree.operator`
      | `results.${number}.rule.conditionsType`
      | `results.${number}.rule.conditionType`
      | `results.${number}.rule.roundingStrategy`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateRuleTags(
  httpClient: HttpClient
): BulkUpdateRuleTagsSignature {
  return (ruleIds: string[], options?: BulkUpdateRuleTagsOptions) =>
    universalBulkUpdateRuleTags(
      ruleIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRuleTagsSignature {
  /**
   * Synchronously update tags on multiple rules.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the rules to update tags for.
   */
  (ruleIds: string[], options?: BulkUpdateRuleTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateRuleTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateRuleTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateRuleTagsByFilter(
  httpClient: HttpClient
): BulkUpdateRuleTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateRuleTagsByFilterOptions
  ) =>
    universalBulkUpdateRuleTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRuleTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple rules according to the specified filter.
   * If a filter isn't specified, this method updates all rules.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which rules to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateRuleTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateRuleTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateRuleTagsByFilterApplicationErrors;
    }
  >;
}

export const onRuleCreated = EventDefinition(
  'wix.service.fees.v1.rule_created',
  true,
  (event: RuleCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.condition.number.value' },
            { path: 'entity.conditionTree.leftCondition.number.value' },
            { path: 'entity.conditionTree.rightCondition.number.value' },
            { path: 'entity.conditionOptions.number.value' },
            { path: 'entity.conditionTreeOptions.leftCondition.number.value' },
            { path: 'entity.conditionTreeOptions.rightCondition.number.value' },
          ],
        },
      ])
    )
)<RuleCreatedEnvelope>();
export const onRuleDeleted = EventDefinition(
  'wix.service.fees.v1.rule_deleted',
  true,
  (event: RuleDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.condition.number.value' },
            { path: 'undefined.conditionTree.leftCondition.number.value' },
            { path: 'undefined.conditionTree.rightCondition.number.value' },
            { path: 'undefined.conditionOptions.number.value' },
            {
              path: 'undefined.conditionTreeOptions.leftCondition.number.value',
            },
            {
              path: 'undefined.conditionTreeOptions.rightCondition.number.value',
            },
          ],
        },
      ])
    )
)<RuleDeletedEnvelope>();
export const onRuleUpdated = EventDefinition(
  'wix.service.fees.v1.rule_updated',
  true,
  (event: RuleUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.condition.number.value' },
            { path: 'entity.conditionTree.leftCondition.number.value' },
            { path: 'entity.conditionTree.rightCondition.number.value' },
            { path: 'entity.conditionOptions.number.value' },
            { path: 'entity.conditionTreeOptions.leftCondition.number.value' },
            { path: 'entity.conditionTreeOptions.rightCondition.number.value' },
            { path: 'modifiedFields.condition.number.value' },
            { path: 'modifiedFields.conditionTree.leftCondition.number.value' },
            {
              path: 'modifiedFields.conditionTree.rightCondition.number.value',
            },
            { path: 'modifiedFields.conditionOptions.number.value' },
            {
              path: 'modifiedFields.conditionTreeOptions.leftCondition.number.value',
            },
            {
              path: 'modifiedFields.conditionTreeOptions.rightCondition.number.value',
            },
          ],
        },
      ])
    )
)<RuleUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddressLocation,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateRulesOptions,
  BulkCreateRulesRequest,
  BulkCreateRulesResponse,
  BulkDeleteRulesRequest,
  BulkDeleteRulesResponse,
  BulkRuleResult,
  BulkUpdateRuleTagsByFilterOptions,
  BulkUpdateRuleTagsByFilterRequest,
  BulkUpdateRuleTagsByFilterResponse,
  BulkUpdateRuleTagsOptions,
  BulkUpdateRuleTagsRequest,
  BulkUpdateRuleTagsResponse,
  BulkUpdateRuleTagsResult,
  BulkUpdateRulesOptions,
  BulkUpdateRulesRequest,
  BulkUpdateRulesResponse,
  CalculateServiceFeesOptions,
  CalculateServiceFeesRequest,
  CalculateServiceFeesResponse,
  CalculatedFee,
  CommonBulkActionMetadata,
  CommonItemMetadata,
  CommonMoney,
  Condition,
  ConditionTree,
  ConditionTreeLeftConditionNodeOneOf,
  ConditionTreeRightConditionNodeOneOf,
  ConditionType,
  ConditionValueOneOf,
  ConditionsType,
  CreateRuleRequest,
  CreateRuleResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteRuleRequest,
  DeleteRuleResponse,
  DeliveryLogistics,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExpectedFieldType,
  ExpectedType,
  ExtendedFields,
  GetRuleRequest,
  GetRuleResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  List,
  ListRulesOptions,
  ListRulesRequest,
  ListRulesResponse,
  Locale,
  MaskedRule,
  MessageEnvelope,
  Money,
  Operation,
  Operator,
  Order,
  Platform,
  PlatformValue,
  PriceSummary,
  QueryRulesRequest,
  QueryRulesResponse,
  RestoreInfo,
  RoundingStrategy,
  Rule,
  RuleConditionTypeOptionsOneOf,
  RuleConditionsOneOf,
  RuleCreatedEnvelope,
  RuleDeletedEnvelope,
  RuleQuerySpec,
  RuleRequirementsOneOf,
  RuleTaxesOneOf,
  RuleUpdatedEnvelope,
  RuleValueOneOf,
  RulesQueryBuilder,
  RulesQueryResult,
  ShippingInformation,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  Type,
  UpdateRule,
  UpdateRuleRequest,
  UpdateRuleResponse,
  Value,
  WebhookIdentityType,
  _Number,
  utils,
} from './service-fees-v1-rule-service-fees.universal.js';
